import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import SummaryCard from "../components/SummaryCard";
import QuotationTable from "../components/QuotationTable";
import { apiURL } from "../config/config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import _ from "lodash";
import quotationTypes from "../data/quotationTypes.json";
import DashboardFilterBar from "../components/DashboardFilterBar";
import LoadingOverlay from "../components/LoadingOverlay";
import PaginationControls from "../components/PaginationControls";
import ReportChart from "../components/ReportChart";
import Select from "react-select";
import { canViewReportChart } from "../utils/buttonVisibility";
import { FiClock, FiFileText } from "react-icons/fi";
import { quotationMatchesSearch } from "../utils/quotationSearch";
import {
  getCachedQuotations,
  setCachedQuotations,
} from "../utils/quotationCache";

const Dashboard = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const company = user?.company?.toUpperCase() || "";

  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [allQuotations, setAllQuotations] = useState([]); // เก็บข้อมูลทั้งหมด
  const [reportData, setReportData] = useState([]);
  const [isReportPanelOpen, setIsReportPanelOpen] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [hasLoadedClients, setHasLoadedClients] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [clientList, setClientList] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]); // Array ของ clientIds
  const [grandTotal, setGrandTotal] = useState(0);
  const [useBeforeVat, setUseBeforeVat] = useState(true);

  const years = [
    currentYear - 3,
    currentYear - 2,
    currentYear - 1,
    currentYear,
    currentYear + 1,
  ];
  const [isDark, setIsDark] = useState(
    localStorage.getItem("theme") === "dark",
  );

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? "#1F2937" : "#ffffff",
      borderColor: isDark ? "#4B5563" : "#d1d5db",
      color: isDark ? "#fff" : "#000",
      boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "none",
      "&:hover": {
        borderColor: isDark ? "#6B7280" : "#9ca3af",
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? "#1F2937" : "#ffffff",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? isDark
          ? "#374151"
          : "#E5E7EB"
        : isDark
          ? "#1F2937"
          : "#ffffff",
      color: isDark ? "#fff" : "#000",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? "#fff" : "#000",
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? "#9CA3AF" : "#6B7280",
    }),
    input: (base) => ({
      ...base,
      color: isDark ? "#fff" : "#000",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDark ? "#374151" : "#E5E7EB",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? "#fff" : "#000",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? "#fff" : "#000",
      ":hover": {
        backgroundColor: isDark ? "#EF4444" : "#DC2626",
        color: "#fff",
      },
    }),
  };

  // 👇 เพิ่ม state สำหรับตัวเลขจาก API summary
  const [summaryCounts, setSummaryCounts] = useState({
    totalQuotations: 0,
    pendingCount: 0,
    approvedCount: 0,
  });

  const availableTypes =
    company && quotationTypes.companies[company]
      ? Object.entries(quotationTypes.companies[company]).map(
          ([label, value]) => ({
            label,
            value,
          }),
        )
      : [];

  // ✅ ดึง summary counts จาก API (เร็ว ไม่ต้องรอรายการ QT)
  const fetchSummaryCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${apiURL}quotations/summary?year=${selectedYear}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data || {};
      setSummaryCounts({
        totalQuotations: data.total ?? 0,
        pendingCount: data.pending ?? 0,
        approvedCount: data.approved ?? 0,
      });
    } catch (err) {
      console.error("Failed to fetch summary counts:", err);
    }
  }, [selectedYear]);

  useEffect(() => {
    const handler = () => {
      setIsDark(localStorage.getItem("theme") === "dark");
    };

    window.addEventListener("theme-change", handler);
    return () => window.removeEventListener("theme-change", handler);
  }, []);

  const fetchClients = useCallback(async () => {
    if (hasLoadedClients || isLoadingClients) return;

    setIsLoadingClients(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${apiURL}clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientList(res.data);
      setHasLoadedClients(true);
    } catch (err) {
      console.error("Error loading clients:", err);
    } finally {
      setIsLoadingClients(false);
    }
  }, [hasLoadedClients, isLoadingClients]);

  // ✅ โหลดทั้งหมดในพื้นหลัง (ใช้สำหรับ filter/excel + เป็น fallback summary)
  const fetchAllQuotations = useCallback(
    async (year) => {
      const cachedQuotations = getCachedQuotations(year);
      if (cachedQuotations) {
        setAllQuotations(cachedQuotations);
        setLoading(false);
      }

      setLoading(!cachedQuotations);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiURL}quotations?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllQuotations(response.data);
        setCachedQuotations(year, response.data || []);
      } catch (error) {
        console.error("Error fetching quotations:", error);
        if (!cachedQuotations) {
          toast.error("Failed to fetch quotations ❌");
        }
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
  fetchSummaryCounts(); // ✅ refetch เมื่อเปลี่ยนปี
}, [fetchSummaryCounts]);


  useEffect(() => {
    setCurrentPage(1);
    fetchAllQuotations(selectedYear);
  }, [selectedYear, fetchAllQuotations]);

  // ถ้า API summary พัง → fallback จาก allQuotations (คำนวณเอง)
  useEffect(() => {
    const noApiData =
      summaryCounts.totalQuotations === 0 &&
      summaryCounts.pendingCount === 0 &&
      summaryCounts.approvedCount === 0; // เพิ่มเช็ค approved ด้วย

    if (noApiData && allQuotations.length > 0) {
      const pending = allQuotations.filter(
        (q) => q.approvalStatus === "Pending",
      ).length;
      const approved = allQuotations.filter(
        (q) => q.approvalStatus === "Approved",
      ).length;

      setSummaryCounts({
        totalQuotations: allQuotations.length,
        pendingCount: pending,
        approvedCount: approved,
      });
    }
  }, [allQuotations, summaryCounts]);

  useEffect(() => {
    setCurrentPage(1); // reset ไปหน้าแรกเมื่อมีการ filter ใหม่
  }, [searchTerm, startDate, endDate, selectedType, selectedStatus]);

  useEffect(() => {
    let result = allQuotations;

    if (startDate || endDate) {
      result = result.filter((qt) => {
        const docDate = new Date(qt.documentDate);
        return (
          (!startDate || docDate >= startDate) &&
          (!endDate || docDate <= endDate)
        );
      });
    }

    if (searchTerm) {
      result = result.filter((qt) => quotationMatchesSearch(qt, searchTerm));
    }

    if (selectedType) {
      result = result.filter((qt) => qt.type === selectedType);
    }

    if (selectedStatus) {
      result = result.filter((qt) => qt.approvalStatus === selectedStatus);
    }

    // pagination ด้านหน้า
    const pageSize = 20;
    const total = Math.ceil(result.length / pageSize) || 1;
    const startIdx = (currentPage - 1) * pageSize;
    const paginated = result.slice(startIdx, startIdx + pageSize);

    setFilteredQuotations(paginated);
    setTotalPages(total);
  }, [
    startDate,
    endDate,
    searchTerm,
    selectedType,
    selectedStatus,
    allQuotations,
    currentPage,
    selectedYear,
  ]);
  useEffect(() => {
    if (!canViewReportChart() || !isReportPanelOpen) return;

    const fetchReport = async () => {
      setIsReportLoading(true);
      try {
        const token = localStorage.getItem("token");

        const clientIdsParam =
          selectedClients.length > 0
            ? `&clientIds=${selectedClients.join(",")}`
            : "";

        const res = await axios.get(
          `${apiURL}reports/department-spending?year=${selectedYear}${clientIdsParam}&useBeforeVat=${
            useBeforeVat ? "true" : "false"
          }`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const chartData = res.data.departments.map((item) => ({
          department: item.department,
          totalSpending: item.totalSpending,
        }));

        setReportData(chartData);
        setGrandTotal(res.data.grandTotal || 0);
      } catch (err) {
        console.error("Error fetching report:", err);
      } finally {
        setIsReportLoading(false);
      }
    };

    fetchReport();
  }, [isReportPanelOpen, selectedYear, selectedClients, useBeforeVat]);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredQuotations].sort((a, b) => {
      if (a[key] < b[key]) return direction === "ascending" ? -1 : 1;
      if (a[key] > b[key]) return direction === "ascending" ? 1 : -1;
      return 0;
    });

    setFilteredQuotations(sorted);
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? " ↑" : " ↓";
    }
    return "";
  };

  const exportToExcel = async (format = "xlsx") => {
    try {
      let data = [...(allQuotations || [])];

      // Apply filters manually อีกครั้ง (เผื่อ search/type/status/date)
      if (startDate || endDate) {
        data = data.filter((qt) => {
          const docDate = new Date(qt.documentDate);
          return (
            (!startDate || docDate >= startDate) &&
            (!endDate || docDate <= endDate)
          );
        });
      }

      if (searchTerm) {
        data = data.filter((qt) => quotationMatchesSearch(qt, searchTerm));
      }

      if (selectedType) {
        data = data.filter((qt) => qt.type === selectedType);
      }

      if (selectedStatus) {
        data = data.filter((qt) => qt.approvalStatus === selectedStatus);
      }

      if (data.length === 0) {
        toast.error("ไม่มีข้อมูลสำหรับ Export ❌");
        return;
      }

      const excelData = data.map((qt) => {
        const formattedDocumentDate = qt.documentDate
          ? new Date(qt.documentDate).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })
          : "-";

        const formatNumber = (num) => (_.isNumber(num) ? _.round(num, 2) : "-");
        const companyPrefix = process.env.REACT_APP_COMPANY_PREFIX || "OPTX";

        let rowData = {
          สถานะ: qt.approvalStatus || "-",
          PDF: "",
          เลขที่เอกสาร: `${companyPrefix}(${qt.type || "M"})-${new Date(
            qt.documentDate,
          ).getFullYear()}-${qt.runNumber}`,
          รอบการอนุมัติ: "",
          ผู้ขออนุมัติ: qt.salePerson || "-",
          หมายเหตุจากผู้ขออนุมัติ: "",
          "สายงาน/ทีม": qt.teamGroup,
          Idสายงาน: "",
          ชื่อสายงาน: "",
          ผู้อนุมัติ: "",
          การอนุมัติ: "",
          โน๊ตอนุมัติ: "",
          วันที่เอกสาร: formattedDocumentDate,
          พนักงานขาย: qt.salePerson || "-",
          ชื่อลูกค้า: qt.clientId?.customerName || "-",
          ที่อยู่: qt.clientId?.address || "-",
          เลขประจำตัวผู้เสียภาษี:
            format === "csv"
              ? `'${qt.clientId?.taxIdentificationNumber || "-"}`
              : qt.clientId?.taxIdentificationNumber || "-",
          "รหัส สาขา":
            format === "csv"
              ? `'${qt.clientId?.branchCode || "00000"}`
              : qt.clientId?.branchCode || "00000",
          หมายเลขโทรศัพท์ติดต่อ: qt.clientId?.contactPhoneNumber || "-",
          ชื่อโครงการ: qt.projectName || "-",
          วันที่จัดงาน:
            format === "csv"
              ? `'${
                  qt.period ||
                  (qt.documentDate
                    ? new Date(qt.documentDate).toLocaleString("en-US", {
                        month: "short",
                        year: "numeric",
                      })
                    : "-")
                }`
              : qt.period ||
                (qt.documentDate
                  ? new Date(qt.documentDate).toLocaleString("en-US", {
                      month: "short",
                      year: "numeric",
                    })
                  : "-"),
          รวม: formatNumber(qt.total),
          ส่วนลด: formatNumber(qt.discount),
          จำนวนเงินก่อนภาษี: formatNumber(qt.amountBeforeTax),
          ภาษีมูลค่าเพิ่ม: formatNumber(qt.vat),
          จำนวนเงินสุทธิ: formatNumber(qt.netAmount),
          รายละเอียดการชำระเงิน: `ระยะเวลา Credit Term ${
            qt.CreditTerm || 0
          } วัน`,
        };

        let itemsData = {};
        qt.items.forEach((item, index) => {
          itemsData[`รายละเอียด (${index + 1})`] = item.description || "-";
          itemsData[`หน่วยนับ (${index + 1})`] = _.toString(item.unit || "-");
          itemsData[`ราคาต่อหน่วย (${index + 1})`] = formatNumber(
            item.unitPrice,
          );
          itemsData[`จำนวนเงิน (${index + 1})`] = formatNumber(item.amount);
        });

        const lastIndex = qt.items.length;
        itemsData[`รายละเอียด (${lastIndex + 1})`] = `Fee` || "-";
        itemsData[`หน่วยนับ (${lastIndex + 1})`] = "1";
        itemsData[`ราคาต่อหน่วย (${lastIndex + 1})`] = formatNumber(qt.calFee);
        itemsData[`จำนวนเงิน (${lastIndex + 1})`] = formatNumber(qt.calFee);

        return { ...rowData, ...itemsData };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Quotations");

      // จัด format
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let row = range.s.r; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          const cell = worksheet[cellAddress];
          if (!cell) continue;
          if (
            cell.v &&
            !isNaN(cell.v) &&
            typeof cell.v === "number" &&
            cell.v !== "-"
          ) {
            cell.z = "#,##0.00";
            cell.s = { alignment: { horizontal: "right" } };
          }
          if (typeof cell.v === "string" && cell.v !== "-") {
            cell.t = "s";
          }
        }
      }

      XLSX.writeFile(
        workbook,
        `Quotations_${new Date().toISOString()}.${format}`,
        { bookType: format },
      );
      toast.success("Export สำเร็จ ✅");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Export ล้มเหลว ❌");
    }
  };
  const exportCustomFormatForBA = async (format = "xlsx") => {
    try {
      let data = [...(allQuotations || [])];

      // 🔁 ฟิลเตอร์ซ้ำตาม search, type, date, status
      if (startDate || endDate) {
        data = data.filter((qt) => {
          const docDate = new Date(qt.documentDate);
          return (
            (!startDate || docDate >= startDate) &&
            (!endDate || docDate <= endDate)
          );
        });
      }

      if (searchTerm) {
        data = data.filter((qt) => quotationMatchesSearch(qt, searchTerm));
      }

      if (selectedType) {
        data = data.filter((qt) => qt.type === selectedType);
      }

      if (selectedStatus) {
        data = data.filter((qt) => qt.approvalStatus === selectedStatus);
      }

      if (data.length === 0) {
        toast.error("ไม่มีข้อมูลสำหรับ Export BA ❌");
        return;
      }

      const formatNumber = (num) =>
        _.isNumber(num)
          ? num.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "-";

      const companyPrefix = process.env.REACT_APP_COMPANY_PREFIX || "OPTX";
      // 🔁 แปลงข้อมูลให้แบน (Flat) ตาม items
      const baExportData = [];

      data.forEach((qt) => {
        const quotationNumber = `${companyPrefix}(${qt.type || "M"})-${new Date(
          qt.documentDate,
        ).getFullYear()}-${qt.runNumber}`;

        // 🔁 รายการสินค้าทั่วไป
        qt.items.forEach((item) => {
          baExportData.push({
            เลขที่ใบเสนอราคา: quotationNumber,
            รายละเอียด: item.description || "-",
            จำนวน: _.toString(item.unit || "-"),
            หน่วยนับ: "Unit",
            ราคาต่อหน่วย: formatNumber(item.unitPrice),
            จำนวนเงิน: formatNumber(item.amount),
          });
        });

        // ✅ เพิ่มแถว Fee ต่อท้าย
        const feeLabel =
          companyPrefix === "NW-QT" ? `fee` : `fee(${formatNumber(qt.fee)}%)`;

        baExportData.push({
          เลขที่ใบเสนอราคา: quotationNumber,
          รายละเอียด: feeLabel,
          จำนวน: "1",
          หน่วยนับ: "Unit",
          ราคาต่อหน่วย: formatNumber(qt.calFee),
          จำนวนเงิน: formatNumber(qt.calFee),
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(baExportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "BA Export");

      XLSX.writeFile(
        workbook,
        `BA_Export_${new Date().toISOString()}.${format}`,
        { bookType: format },
      );
      toast.success("Export Detail to BA สำเร็จ ✅");
    } catch (error) {
      console.error("Error exporting Detail to BA:", error);
      toast.error("Export BA ล้มเหลว ❌");
    }
  };

  return (
    <div className="p-4 sm:p-4 relative dark:bg-gray-950 min-h-screen transition-colors duration-300">
      <LoadingOverlay loading={loading} />

      {user && (
        <h1 className="text-2xl font-bold text-gray-800 dark:text-yellow-400 mb-4 transition-colors">
          Welcome back, {user.firstName}!
        </h1>
      )}

      {/* ✅ แทรกกราฟ */}
      {canViewReportChart() && (
        <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4 sm:px-6 mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                Report Overview
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Load the report only when you want to inspect the chart.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsReportPanelOpen((prev) => !prev)}
              className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
            >
              {isReportPanelOpen ? "Hide Report" : "Show Report"}
            </button>
          </div>

          {isReportPanelOpen && (
            <>
              <div className="mt-4 flex flex-col gap-4 md:flex-row">
                <div className="flex-1">
                  <label className="text-gray-800 dark:text-gray-300 font-medium">
                    เลือกลูกค้า (เลือกได้หลายบริษัท)
                  </label>

                  <div className="relative">
                    <Select
                      isMulti
                      options={clientList.map((c) => ({
                        value: c._id,
                        label: c.customerName,
                      }))}
                      value={clientList
                        .filter((c) => selectedClients.includes(c._id))
                        .map((c) => ({ value: c._id, label: c.customerName }))}
                      onChange={(selected) => {
                        setSelectedClients(
                          (selected || []).map((option) => option.value),
                        );
                      }}
                      onMenuOpen={fetchClients}
                      isLoading={isLoadingClients}
                      placeholder={
                        isLoadingClients
                          ? "Loading clients..."
                          : "Select clients..."
                      }
                      className="text-black dark:text-white"
                      styles={customSelectStyles}
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <label className="text-gray-800 dark:text-gray-300 font-medium">
                    ปี
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full p-2 border dark:bg-gray-800 rounded"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                {isReportLoading ? (
                  <div className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    Loading report...
                  </div>
                ) : (
                  <ReportChart
                    data={reportData}
                    grandTotal={grandTotal}
                    useBeforeVat={useBeforeVat}
                    setUseBeforeVat={setUseBeforeVat}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ✅ Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <SummaryCard
          title="Pending Approvals"
          value={summaryCounts.pendingCount}
          icon={<FiClock size={20} />}
          variant="indigo"
          href="/approvals"
        />
        <SummaryCard
          title="Total Quotations"
          value={summaryCounts.totalQuotations}
          icon={<FiFileText size={20} />}
          variant="green"
          href="/quotations"
        />
      </div>

      {/* ✅ Data Table Section */}
      <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4 sm:px-6 transition-colors duration-300">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
            <FiFileText className="text-gray-500 dark:text-indigo-400" />
            <span>All Quotations</span>
          </div>

          <DashboardFilterBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            availableTypes={availableTypes}
            onClear={() => {
              setStartDate(null);
              setEndDate(null);
              setSearchTerm("");
              setSelectedType("");
              setSelectedStatus("");
              setSelectedYear(currentYear);
            }}
            onExport={(format) => exportToExcel(format)}
            onCustomExport={(format) => exportCustomFormatForBA(format)} // 🆕 ปุ่มใหม่
          />
        </div>

        {filteredQuotations.length > 0 ? (
          <div className="overflow-x-auto">
            <QuotationTable
              quotations={filteredQuotations}
              sortConfig={sortConfig}
              handleSort={handleSort}
              getSortIndicator={getSortIndicator}
            />
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8 transition-colors">
            No quotations found.
          </p>
        )}

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
};

export default Dashboard;
